import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { useAuth } from '../auth/AuthProvider';

/**
 * What a signed-in person sees when there is nowhere for them to go.
 *
 * Signing in is not tenant-scoped — identities are global (ADR 0009) — so
 * suspending a tenant has never stopped its people authenticating. Until this
 * existed they landed in the full application and every request came back 403:
 * a working-looking product that failed everywhere, which reads as "the system
 * is broken" rather than "your organisation's access is paused".
 *
 * Three distinguishable situations, because the right thing to do differs:
 *
 *  - SUSPENDED — usually temporary, often a billing question. Their data is
 *    untouched and their vehicles are still being recorded, which is the first
 *    thing anybody wants to know and the last thing they assume.
 *  - ARCHIVED — deliberate and not coming back. Saying "suspended" here would
 *    promise a return that nobody intends.
 *  - No membership at all — a real account that nobody has given access to. The
 *    fix is somebody else's, so the message names who.
 */
export function NoUsableTenantPage() {
  const { user, logout } = useAuth();
  const memberships = user?.memberships ?? [];

  const suspended = memberships.filter((m) => m.tenantState === 'SUSPENDED');
  const archived = memberships.filter((m) => m.tenantState === 'ARCHIVED');

  /*
   * Who to ask, which is not always the same person.
   *
   * A BRANCH is suspended and reactivated by an administrator of its
   * organisation — the controls live on the parent's Sub-tenants page and a
   * platform operator is not normally involved. A top-level tenant has no
   * parent, so there it really is the platform. Saying "your platform
   * administrator" in both cases sends most readers to the wrong person, which
   * is the exact failure this screen exists to prevent.
   */
  const whoFor = (rows: typeof memberships) =>
    rows.every((m) => m.parentTenantId !== null)
      ? 'an administrator of your organisation'
      : 'your platform administrator';

  const { icon, title, body } =
    suspended.length > 0
      ? {
          icon: <PauseCircleOutlinedIcon sx={{ fontSize: 44 }} color="warning" />,
          title:
            suspended.length === 1
              ? `${suspended[0]!.tenantName} is suspended`
              : 'Your organisations are suspended',
          body: `Nothing has been lost. Data is retained in full and vehicles carry on reporting into it, so nothing is missing when access resumes. Suspension is usually an administrative or billing matter — ${whoFor(suspended)} can lift it.`,
        }
      : archived.length > 0
        ? {
            icon: <Inventory2OutlinedIcon sx={{ fontSize: 44 }} color="action" />,
            title:
              archived.length === 1
                ? `${archived[0]!.tenantName} has been archived`
                : 'Your organisations have been archived',
            body: `Archiving is deliberate and its records are kept, but it is not something that resumes on its own. If this is unexpected, ${whoFor(archived)} can tell you why and restore it.`,
          }
        : {
            icon: <PersonOffOutlinedIcon sx={{ fontSize: 44 }} color="action" />,
            title: 'You do not belong to a tenant yet',
            body: 'Your account exists and your sign-in worked. What is missing is access to an organisation, which an administrator of that organisation grants — it is not something you can do from here.',
          };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 560 }} elevation={0} variant="outlined">
        <Stack spacing={2}>
          {icon}
          <Typography variant="h5">{title}</Typography>
          <Typography variant="body1" color="text.secondary">
            {body}
          </Typography>
          {/* Named, so somebody can quote it. "Ask your administrator" is
              advice; "ask them about Cardiff depot" is actionable. */}
          {memberships.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Signed in as {user?.email} · {memberships.map((m) => m.tenantName).join(', ')}
            </Typography>
          )}
          <Box>
            <Button variant="outlined" onClick={logout}>
              Sign out
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
